import { DEFAULT_TEMPLATES, devicesFor, printLabels, SHEET_LAYOUTS, SheetLayoutKey } from '@iws/pos-devices'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Segmented } from '@/components/ui'
import { ProductModel } from '@/http/models'
import { deviceModels, readSetup } from '@/services/devices/setup'
import { codeOf, labelData, useReceiptHeader } from '@/services/printer'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

type Kind = 'product_label' | 'shelf_label'

const SHEET_LABEL: Record<SheetLayoutKey, string> = {
  labels_3x8: 'A4, 3 × 8 labels',
  labels_4x10: 'A4, 4 × 10 labels',
  cards_2x5: 'A4, 2 × 5 cards',
}

/**
 * Product and shelf labels (POS.md §18.9): pick products and copies, then print on a label printer (`label` role) or on
 * A4 label sheets (`document` role, or any printer through the browser).
 */
export const LabelsDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const header = useReceiptHeader()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<PosModel.Product[]>([])
  const [items, setItems] = useState<{ product: PosModel.Product; copies: number }[]>([])
  const [kind, setKind] = useState<Kind>('product_label')
  const [targetId, setTargetId] = useState('')
  const [sheet, setSheet] = useState<SheetLayoutKey>('labels_3x8')
  const [busy, setBusy] = useState(false)

  // Label printers first, then A4 printers; one device may sit on both roles.
  const targets = useMemo(() => {
    if (!open) return []
    const setup = readSetup()
    const all = [...devicesFor(setup, 'label', deviceModels()), ...devicesFor(setup, 'document', deviceModels())]
    return all.filter((d, i) => all.findIndex((x) => x.device.id === d.device.id) === i)
  }, [open])
  const target = targets.find((t) => t.device.id === targetId) ?? targets[0]
  const onSheet = !!target && (target.device.transport === 'browser' || target.model.protocol === 'html')

  useEffect(() => {
    if (!open) return
    setItems([])
    setSearch('')
  }, [open])

  useEffect(() => {
    if (!search.trim()) return setResults([])
    let live = true
    ProductModel.getList({ search }).then((res) => live && setResults(res.success && res.data ? res.data.items.slice(0, 8) : []))
    return () => {
      live = false
    }
  }, [search])

  const add = (product: PosModel.Product) => {
    setItems((list) => (list.some((i) => i.product._id === product._id) ? list : [...list, { product, copies: 1 }]))
    setSearch('')
  }

  const print = async () => {
    if (!target) return toast.error('No label or A4 printer is set up. Open Devices to add one.')
    setBusy(true)
    try {
      await printLabels(
        target,
        DEFAULT_TEMPLATES[kind],
        items.map((i) => ({ data: labelData(i.product, header.name), copies: i.copies })),
        onSheet ? sheet : undefined
      )
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const total = items.reduce((n, i) => n + i.copies, 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Print labels</DialogTitle>
          <DialogDescription>Product or shelf labels with the price and a barcode that scans at the POS.</DialogDescription>
        </DialogHeader>

        <Segmented<Kind>
          value={kind}
          onChange={setKind}
          options={[
            { value: 'product_label', label: 'Product label' },
            { value: 'shelf_label', label: 'Shelf label' },
          ]}
        />

        <div className="relative">
          <Input placeholder="Search products by name, SKU or barcode" onCameraScan={setSearch} value={search} onChange={(e) => setSearch(e.target.value)} />
          {results.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-card">
              {results.map((p) => (
                <li key={p._id}>
                  <button type="button" className="flex w-full justify-between px-3 py-2 text-left text-sm hover:bg-fill" onClick={() => add(p)}>
                    <span className="truncate">{p.name}</span>
                    <span className="tabular-nums text-muted-foreground">{formatMoney(p.price)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="divide-y divide-border-subtle rounded-md border border-border">
          {items.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Add products to print.</p>}
          {items.map((i) => (
            <div key={i.product._id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate">{i.product.name}</span>
              <span className="text-xs text-muted-foreground">{codeOf(i.product)}</span>
              <Input
                type="number"
                min={0}
                aria-label={`Copies of ${i.product.name}`}
                className="w-20 text-right tabular-nums"
                value={i.copies}
                onChange={(e) => {
                  const copies = Math.max(0, Math.floor(Number(e.target.value) || 0))
                  setItems((list) => list.map((x) => (x.product._id === i.product._id ? { ...x, copies } : x)))
                }}
              />
              <button
                type="button"
                aria-label={`Remove ${i.product.name}`}
                className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-fill"
                onClick={() => setItems((list) => list.filter((x) => x.product._id !== i.product._id))}
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="labels-printer">Printer</Label>
            <select
              id="labels-printer"
              value={target?.device.id ?? ''}
              onChange={(e) => setTargetId(e.target.value)}
              className="h-9 rounded-sm border border-input bg-card px-3 text-sm text-ink"
            >
              {targets.length === 0 && <option value="">None set up</option>}
              {targets.map((t) => (
                <option key={t.device.id} value={t.device.id}>
                  {t.device.name}
                </option>
              ))}
            </select>
          </div>
          {onSheet && (
            <div className="grid gap-1.5">
              <Label htmlFor="labels-sheet">Sheet</Label>
              <select
                id="labels-sheet"
                value={sheet}
                onChange={(e) => setSheet(e.target.value as SheetLayoutKey)}
                className="h-9 rounded-sm border border-input bg-card px-3 text-sm text-ink"
              >
                {(Object.keys(SHEET_LAYOUTS) as SheetLayoutKey[]).map((k) => (
                  <option key={k} value={k}>
                    {SHEET_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <DialogFooter>
          <PosButton variant="outline" size="touch" onClick={() => onOpenChange(false)}>
            Close
          </PosButton>
          <PosButton size="touch" disabled={busy || total === 0 || !target} onClick={print}>
            Print {total > 0 ? `${total} label${total === 1 ? '' : 's'}` : ''}
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
